import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { DamageWaiverApi } from "../../api/DamageWaiverApi";
import type { CreateDamageWaiverType } from "../../generated/dto/CreateDamageWaiverType";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverMember } from "../../generated/dto/DamageWaiverMember";
import type { DamageWaiverMemberFetchOptions } from "../../generated/dto/DamageWaiverMemberFetchOptions";
import type { DamageWaiverMemberSearchResult } from "../../generated/dto/DamageWaiverMemberSearchResult";
import type { DamageWaiverSearchParams } from "../../generated/dto/DamageWaiverSearchParams";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DamageWaiverStaffSearchResult } from "../../generated/dto/DamageWaiverStaffSearchResult";
import type { DamageWaiverTypeMember } from "../../generated/dto/DamageWaiverTypeMember";
import type { DamageWaiverTypeSearchParams } from "../../generated/dto/DamageWaiverTypeSearchParams";
import type { DamageWaiverTypeStaff } from "../../generated/dto/DamageWaiverTypeStaff";
import type { DamageWaiverTypeStaffSearchResult } from "../../generated/dto/DamageWaiverTypeStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { UpdateDamageWaiverType } from "../../generated/dto/UpdateDamageWaiverType";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DamageWaiverApiClient implements DamageWaiverApi {
  constructor(private readonly client = useClient()) {}

  async getDamageWaiverMember(id: number, options?: DamageWaiverMemberFetchOptions): Promise<DamageWaiverMember> {
    const { data } = await this.client.get<unknown>(`/damagewaiver/member/${id}`, { params: serializeCbiDto(options, "generated.dto.DamageWaiverMemberFetchOptions") });
    return deserializeCbiDto<DamageWaiverMember>(data, "generated.dto.DamageWaiverMember");
  }

  async getDamageWaiverStaff(id: number, options?: DamageWaiverFetchOptions): Promise<DamageWaiverStaff> {
    const { data } = await this.client.get<unknown>(`/damagewaiver/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.DamageWaiverFetchOptions") });
    return deserializeCbiDto<DamageWaiverStaff>(data, "generated.dto.DamageWaiverStaff");
  }

  async getDamageWaiverTypeStaff(id: number): Promise<DamageWaiverTypeStaff> {
    const { data } = await this.client.get<unknown>(`/damagewaivertype/staff/${id}`);
    return deserializeCbiDto<DamageWaiverTypeStaff>(data, "generated.dto.DamageWaiverTypeStaff");
  }

  async getAllDamageWaiverTypeStaffs(): Promise<Array<DamageWaiverTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/damagewaivertype/staff/all`);
    return deserializeCbiDto<Array<DamageWaiverTypeStaff>>(data, "generated.dto.DamageWaiverTypeStaff");
  }

  async createDamageWaiverTypeStaff(source: CreateDamageWaiverType): Promise<DamageWaiverTypeStaff> {
    const { data } = await this.client.post<unknown>(`/damagewaivertype/staff`, serializeCbiDto(source, "generated.dto.CreateDamageWaiverType"));
    return deserializeCbiDto<DamageWaiverTypeStaff>(data, "generated.dto.DamageWaiverTypeStaff");
  }

  async updateDamageWaiverTypeStaff(source: UpdateDamageWaiverType): Promise<DamageWaiverTypeStaff> {
    const { data } = await this.client.put<unknown>(`/damagewaivertype/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateDamageWaiverType"));
    return deserializeCbiDto<DamageWaiverTypeStaff>(data, "generated.dto.DamageWaiverTypeStaff");
  }

  async searchDamageWaiverMember(params: DamageWaiverSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/member/search`, serializeCbiDto(params, "generated.dto.DamageWaiverSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DamageWaiverMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverMemberSearchResult>>(result.items, "generated.dto.DamageWaiverMemberSearchResult") };
    return result;
  }

  async searchDamageWaiverStaff(params: DamageWaiverSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/staff/search`, serializeCbiDto(params, "generated.dto.DamageWaiverSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DamageWaiverStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverStaffSearchResult>>(result.items, "generated.dto.DamageWaiverStaffSearchResult") };
    return result;
  }

  async searchDamageWaiverTypeStaff(params: DamageWaiverTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverTypeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/damagewaivertype/staff/search`, serializeCbiDto(params, "generated.dto.DamageWaiverTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DamageWaiverTypeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverTypeStaffSearchResult>>(result.items, "generated.dto.DamageWaiverTypeStaffSearchResult") };
    return result;
  }

  async searchDamageWaiverMemberFull(params: DamageWaiverSearchParams, page: number, perPage: number, options?: DamageWaiverMemberFetchOptions): Promise<PaginationResult<DamageWaiverMember>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/member/search/full`, serializeCbiDto(params, "generated.dto.DamageWaiverSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.DamageWaiverMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<DamageWaiverMember>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverMember>>(result.items, "generated.dto.DamageWaiverMember") };
    return result;
  }

  async searchDamageWaiverStaffFull(params: DamageWaiverSearchParams, page: number, perPage: number, options?: DamageWaiverFetchOptions): Promise<PaginationResult<DamageWaiverStaff>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/staff/search/full`, serializeCbiDto(params, "generated.dto.DamageWaiverSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.DamageWaiverFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<DamageWaiverStaff>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverStaff>>(result.items, "generated.dto.DamageWaiverStaff") };
    return result;
  }

  async searchDamageWaiverTypeStaffFull(params: DamageWaiverTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverTypeStaff>> {
    const { data } = await this.client.post<unknown>(`/damagewaivertype/staff/search/full`, serializeCbiDto(params, "generated.dto.DamageWaiverTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DamageWaiverTypeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<DamageWaiverTypeStaff>>(result.items, "generated.dto.DamageWaiverTypeStaff") };
    return result;
  }

  async getDamageWaiverStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/damagewaiver/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getDamageWaiverStaffDamageWaiverTypeId(id: number): Promise<DamageWaiverTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/damagewaiver/staff/${id}/damagewaivertypeid`);
    return deserializeCbiDto<DamageWaiverTypeStaff | null>(data, "generated.dto.DamageWaiverTypeStaff");
  }

  async purchaseOptionsMember(): Promise<Array<DamageWaiverTypeMember>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/member/purchaseoptions`);
    return deserializeCbiDto<Array<DamageWaiverTypeMember>>(data, "generated.dto.DamageWaiverTypeMember");
  }

  async purchaseOptionsPublic(): Promise<Array<DamageWaiverTypeMember>> {
    const { data } = await this.client.post<unknown>(`/damagewaiver/public/purchaseoptions`);
    return deserializeCbiDto<Array<DamageWaiverTypeMember>>(data, "generated.dto.DamageWaiverTypeMember");
  }
}

export const useDamageWaiverApi = (): DamageWaiverApi => new DamageWaiverApiClient();
